// Sizing for the TIMELINE variant of the multiway distribution marker, and the width it
// occupies on screen.
//
// The tokens live here rather than inside MultiwayMarker because two callers need them:
// the component, to render, and ForecastLane, to place the "Generating Forecast" status
// beside the bars without overlapping them. A copy at the call site would drift the first
// time a bar got wider; a non-component export from the component file trips
// react-refresh/only-export-components.
//
// Pure module (no React) — safe to import from anywhere.

import { BINARY_NO_LABEL, BINARY_YES_LABEL, formatBucketLabel } from "../forecast/multiway.js";

/**
 * Size tokens for the floating, on-spine marker (the "timeline" variant).
 *
 * ── The column model ────────────────────────────────────────────────────────
 * Three columns, left to right: BAR | PERCENT | LABEL. Every column
 * has a FIXED width, which is what lets the winning row render larger without
 * breaking alignment — it grows in TYPE SIZE ONLY, never in geometry, so bar left
 * edges, percent right edges, and label left edges line up down the whole block
 * regardless of which row won.
 *
 * The `win*` tokens are the winning row's counterparts, and there are deliberately
 * only two — the percent and label type sizes. Everything geometric is shared: same
 * bar height, same row height, same track length, same column widths.
 *
 * There is intentionally no winning-row BAR HEIGHT or ROW HEIGHT token. A thicker
 * winning bar reads as a larger quantity than the number beside it states, and when
 * the leader sits in the MIDDLE of an ordered set of bins it also breaks the even
 * rhythm of the stack, so the eye finds the bulge before it finds the number.
 * Emphasis belongs in the type, which carries no quantitative meaning. The absence
 * of the tokens is the enforcement: with none to set, a later edit cannot quietly
 * scale the bar back up.
 */
export const TIMELINE_TOKENS = Object.freeze({
  rowH: 22,
  // Track length is SHARED by every row, winner included. This is the token that
  // must never gain a `win` variant: a longer winning bar reads as a larger
  // probability than the number beside it states.
  barTrack: 86,
  barH: 10,
  pctSize: 11,
  winPctSize: 18,
  labelSize: 10,
  winLabelSize: 14,
  // Gap from the bar's right edge to the percent column. Deliberately tight — the
  // number belongs to the bar it measures, and reads as its own column when pushed off.
  //
  // NOTE this is the gap to the COLUMN, not to every glyph. Because percentages are
  // right-aligned in a fixed-width column, a short value ("1%") still sits further from
  // the bar than a wide one ("100%") by the slack it does not fill. That is inherent to
  // right-alignment, not a stray margin: the alternative is ragged right edges.
  pctGap: 3,
  labelGap: 12,
  railW: 2,
  padL: 7,
  padR: 12,
  badgeSize: 9,
  // Text metrics the width estimate needs. These MIRROR the component's inline styles —
  // the bucket label's letterSpacing and the settled tag's. Change one there, change it here.
  labelTracking: 1,
  tagTracking: 1,
});

/** Mono advance per character, as a fraction of font size. */
const MONO_ADVANCE = 0.6;

/** Width of a mono run at a given size and letter-spacing, in px. */
function runWidth(text, fontSize, tracking) {
  return String(text ?? "").length * (fontSize * MONO_ADVANCE + tracking);
}

/**
 * Width of the fixed percent column, in px.
 *
 * Sized for the widest value the column can ever hold — ">99.9%" at the WINNING row's
 * type size — so the column never resizes as probabilities move, and a non-winning
 * row's smaller number right-aligns into the same edge.
 *
 * Exported because the component and the width estimate both need it, and this is the
 * class of duplicated constant that has already drifted once in this module's history.
 *
 * @param {typeof TIMELINE_TOKENS} t
 * @returns {number}
 */
export function pctColumnWidth(t) {
  return Math.ceil(runWidth(">99.9%", t.winPctSize, 0)) + 4;
}

/**
 * Approximate rendered width of the timeline marker, in px.
 *
 * An ESTIMATE, and deliberately a HIGH one. Callers use it to reserve space — to place the
 * generating label clear of the bars, and to keep the winning row out from under the chat
 * console — so over-reserving costs a little whitespace while under-reserving clips the
 * thing it was meant to protect. It therefore measures the longest label at the WINNING
 * row's type size, which only one row actually uses.
 *
 * Text is measured as mono advance ≈ 0.6em plus letter-spacing, so this is exact enough to
 * sit something beside the block, not to align to its edge. Labels are measured AFTER
 * `formatBucketLabel`, which can add thousands separators.
 *
 * @param {Array<{label?: string}>} buckets
 * @param {string} [badge] Settled tag on the winning row ("Resolved" and friends).
 * @returns {number}
 */
export function timelineMarkerWidth(buckets, badge = "") {
  const T = TIMELINE_TOKENS;
  const longest = (buckets || []).reduce(
    (m, b) => Math.max(m, formatBucketLabel(b?.label ?? "").length),
    0,
  );
  // The settled tag ("✓ RESOLVED") trails the winning row's label when there is one.
  // Measured at the badge size plus the check glyph and its gap.
  const tag = badge ? T.badgeSize + 4 + runWidth(badge, T.badgeSize, T.tagTracking) + 8 : 0;
  return (
    T.padL +
    T.railW +
    T.barTrack +
    T.pctGap +
    pctColumnWidth(T) +
    T.labelGap +
    runWidth("x".repeat(longest), T.winLabelSize, T.labelTracking) +
    tag +
    T.padR
  );
}

/**
 * Where the timeline marker's bars sit, for keeping trigger labels out from under them.
 *
 * `barsLeft` is the x every bar's left edge shares: the component's `left: markerX + 8` plus
 * the row's transparent rail and left pad. `halfH` is how far the rows reach above and below
 * the spine — the block is centred on it. The "Unresolved"/"Tied" line under the rows is left
 * out; it adds a few px at the bottom, far from where a label could meet it.
 *
 * @param {number} markerX the forecast line's screen x
 * @param {number} rowCount buckets drawn
 * @returns {{barsLeft: number, halfH: number}}
 */
export function timelineMarkerFootprint(markerX, rowCount) {
  const T = TIMELINE_TOKENS;
  return { barsLeft: markerX + 8 + T.railW + T.padL, halfH: (rowCount * T.rowH) / 2 };
}

/**
 * Px trimmed off the framing reserve, in px.
 *
 * A TRIM rather than a gap, which is the honest description: `timelineMarkerWidth` is
 * deliberately a HIGH estimate (see its docstring), so the reserve already exceeds what the
 * block occupies and the leftover shows up as dead space between the last label and the chat
 * console. Adding air on top of that made the space worse; the fix is to give some back.
 *
 * CALIBRATED BY MEASUREMENT, not derived, and it feeds a rendered gap roughly 1:1 — so any
 * change to the estimate's inputs (track width, type sizes, the mono-advance assumption, the
 * settled-tag allowance) moves that gap too. RE-MEASURE after touching those rather than
 * reasoning about this number on its own.
 */
const FRAMING_TRIM = 35;

/**
 * Space to reserve to the RIGHT of the resolution line, in px, for whatever that line
 * renders — the big % label, or the wider distribution marker.
 *
 * Extracted so the zoom framing and the render agree on ONE number. They did not before:
 * the app derived this from the CURRENTLY ACTIVE forecast, while boot and forecast-selection
 * frame a forecast that has not landed in `active` yet. Those paths therefore framed using
 * the previous forecast's buffer (or, on a cold load, the binary default) and came out too
 * tight — the timeline opened zoomed in further than CENTER, which recomputed it later from
 * the real distribution.
 *
 * Pass the contract being FRAMED, not the active one.
 *
 * `assumeMarker` false returns the narrow %-label reservation. When true (All Multiway, or
 * flags still loading) it measures the marker: from the contract's own buckets when it has
 * them, else the fixed binary pair — over-reserving costs a little right margin for one
 * paint, under-reserving clips the label until the user re-centres.
 *
 * @param {Array<{label?: string}>|null} buckets the framed contract's buckets, or null
 * @param {{assumeMarker?: boolean, badge?: string}} [opts]
 * @returns {number}
 */

export function resolutionRightBufferFor(buckets, { assumeMarker = true, badge } = {}) {
  if (!assumeMarker) return 110;
  const measured =
    Array.isArray(buckets) && buckets.length
      ? buckets
      : [{ label: BINARY_YES_LABEL }, { label: BINARY_NO_LABEL }];
  // +8 is the marker's own offset off the line; FRAMING_TRIM gives back part of the
  // estimate's over-reserve so the block does not sit marooned from the console edge.
  //
  // `badge` is NOT defaulted to "Resolved" any more. It was, and no caller passes one — so
  // every forecast reserved room for a settled row's "✓ RESOLVED" tag, including the live
  // ones that never render it. That reserve is ~70px of the block's width and it was the
  // bulk of the empty space measured between the last label and the console edge.
  //
  // A caller that knows the forecast HAS settled can still pass the badge and get the tag
  // reserved. None does today, so a resolved marker's tag now has less room to the console
  // than it did — the deliberate trade for a tight gap on the common case.
  return Math.max(110, 8 + timelineMarkerWidth(measured, badge) - FRAMING_TRIM);
}
